import { Fragment, useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './api/client';
import { LiveView } from './views/LiveView';
import { ReportingView } from './views/ReportingView';
import { VisaiWidget } from './components/VisaiWidget';
import { DashboardView } from './views/DashboardView';
import { HistoryView } from './views/HistoryView';
import { DatasetView } from './views/DatasetView';
import { AnnotateView } from './views/AnnotateView';
import { TrainingView } from './views/TrainingView';
import { GpuTrainingView } from './views/GpuTrainingView';
import { FleetView } from './views/FleetView';
import { UsersView } from './views/UsersView';
import { CustomersView } from './views/CustomersView';
import { LineView } from './views/LineView';
import { CameraPickerView } from './views/CameraPickerView';
import { LensPickerView } from './views/LensPickerView';
import { ViewErrorBoundary } from './components/ViewErrorBoundary';
import { DemoProvider, useDemo } from './DemoContext';
import { SafetyView } from './views/SafetyView';
import { SafetyLiveView } from './views/SafetyLiveView';
import { SafetyZonesView } from './views/SafetyZonesView';
import { SafetyEventsView } from './views/SafetyEventsView';
import { SafetyDashboardView } from './views/SafetyDashboardView';
import { CatalogueDetail } from './views/CatalogueDetail';
import { ClassesView } from './views/ClassesView';
import { ClassifyView } from './views/ClassifyView';
import { ConfusionView } from './views/ConfusionView';
import { CatalogueView } from './views/CatalogueView';
import { DesignsView } from './views/DesignsView';
import { LabelsView } from './views/LabelsView';
import { CmsView } from './views/CmsView';
import { AnalyticsView } from './views/AnalyticsView';
import { ModelAnalytics } from './views/ModelAnalytics';
import { SettingsView } from './views/SettingsView';
import { LoginView } from './views/LoginView';
import { PortalView } from './views/PortalView';
import { ByoaGuideView } from './views/ByoaGuideView';
import { HeroSwirlView } from './views/HeroSwirlView';
import { ProcGuardView } from './views/ProcGuardView';
import { ProcGuardProcedureEditor } from './views/ProcGuardProcedureEditor';
import { ProcGuardPartsView } from './views/ProcGuardPartsView';
import { ProcGuardCyclesView } from './views/ProcGuardCyclesView';
import { ProcGuardKioskView } from './views/ProcGuardKioskView';
import { ProcGuardDashboardView } from './views/ProcGuardDashboardView';
import { TASKS } from '@visionqc/shared';
import { useTasks } from './api/useTasks';
import { EnvironmentBanner, BANNER_HEIGHT } from './components/EnvironmentBanner';
import { LandingView } from './views/LandingView';
import { Wordmark } from './Wordmark';
import { DEFAULT_DEMO_NAV } from './navItems';
import { AuthContext, authApi, useAuth, type AuthUser } from './api/auth';

function HealthDot() {
  const [ok, setOk] = useState<boolean | null>(null);
  useEffect(() => {
    const tick = () =>
      api
        .health()
        .then((h) => setOk(h.ok))
        .catch(() => setOk(false));
    tick();
    const id = setInterval(tick, 10_000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="nav-link" style={{ fontSize: 14 }}>
      <span className={`health-dot ${ok ? 'ok' : 'bad'}`} />
      {ok === null ? 'Checking…' : ok ? 'Systems OK' : 'Service issue'}
    </div>
  );
}

/** Signed-in user chip + sign out, at the bottom of the nav. */
declare const __BUILD_TIME__: string;

/** Which build this browser actually has — settles stale-cache questions. */
function BuildStamp() {
  const built = typeof __BUILD_TIME__ === 'string' ? new Date(__BUILD_TIME__) : null;
  if (!built) return null;
  return (
    <div
      className="muted"
      style={{ padding: '0 16px 10px', fontSize: 10, letterSpacing: 0.3 }}
      title={`Loaded build: ${built.toISOString()}`}
    >
      build {built.toLocaleDateString()} {built.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </div>
  );
}

function UserChip() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontWeight: 700 }}>{user.name}</div>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        {/* Neutral: a role is what you are, not a verdict on you. */}
        <span className="class-chip neutral">{user.role}</span>
        <button className="btn sm" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * Auth gate: resolves the session once, shows the PIN login when required, and
 * exposes the user to the rest of the app.
 */
export function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authRequired, setAuthRequired] = useState(true);
  const [env, setEnv] = useState<{ environment: string; label: string }>({
    environment: 'production',
    label: '',
  });
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [demo, setDemo] = useState(false);
  const [urls, setUrls] = useState<{ portalUrl: string; adminUrl: string }>({
    portalUrl: '',
    adminUrl: '',
  });

  // The demo wears the ZeroIncidents skin: a light, orange, squared repaint
  // driven entirely by data-brand on <html> (see index.css). Toggling an
  // attribute keeps the theme out of every component and reversible in one line.
  useEffect(() => {
    const el = document.documentElement;
    if (demo) el.setAttribute('data-brand', 'zeroincidents');
    else el.removeAttribute('data-brand');
  }, [demo]);

  const refresh = useCallback(async () => {
    const me = await authApi.me();
    setUser(me.user);
    setAuthRequired(me.authRequired);
    setEnv({ environment: me.environment, label: me.environmentLabel });
    setUrls({ portalUrl: me.portalUrl, adminUrl: me.adminUrl });
    setDemo(me.demo);
    setLoading(false);
  }, []);

  const logout = useCallback(async () => {
    // Signing out is explicit and must stick. On a kiosk/device login the key is
    // in localStorage and would silently sign the machine straight back in on the
    // next load — so an explicit sign-out clears it too. (A real kiosk is brought
    // back by re-opening its provisioning link.)
    try {
      localStorage.removeItem('kiosk.deviceKey');
    } catch {
      /* private browsing — nothing stored to clear */
    }
    await authApi.logout().catch(() => undefined);
    // A full reload rather than a state refresh. A state-only sign-out left the
    // portal on screen in the wild (in-flight sockets, a cached /me, a re-render
    // that did not take); reloading the origin guarantees a clean, logged-out app
    // — bootstrap re-runs with no session and no key and lands on the sign-in page.
    window.location.assign('/');
  }, []);

  /**
   * Kiosk auto-login. An on-site machine is provisioned once by opening a link
   * with ?device=KEY; we store the key and sign in with it. Thereafter, whenever
   * the session has lapsed (they last a shift), the stored key re-signs the
   * machine in on its own, so the screen stays on the customer's portal without
   * anyone touching it. The key lives only in this browser's localStorage.
   */
  const bootstrap = useCallback(async () => {
    let key: string | null = null;
    try {
      const url = new URL(window.location.href);
      const fromUrl = url.searchParams.get('device');
      if (fromUrl) {
        localStorage.setItem('kiosk.deviceKey', fromUrl);
        // Strip the key from the address bar so it is not left on screen or in history.
        url.searchParams.delete('device');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
      key = fromUrl || localStorage.getItem('kiosk.deviceKey');
    } catch {
      /* private browsing / bad URL — fall through to a normal load */
    }

    let me = await authApi.me();
    if (!me.user && key) {
      // A provisioned kiosk with no live session: sign in with its key.
      try {
        await authApi.device(key);
        me = await authApi.me();
      } catch {
        /* revoked or invalid key — land on the normal login instead */
      }
    }
    setUser(me.user);
    setAuthRequired(me.authRequired);
    setEnv({ environment: me.environment, label: me.environmentLabel });
    setUrls({ portalUrl: me.portalUrl, adminUrl: me.adminUrl });
    // Without this the demo skin never engaged on the first screen: only
    // refresh() (which runs after sign-in) was setting it, so the landing and
    // login pages loaded with demo=false and the ZeroIncidents theme off.
    setDemo(me.demo);
    setLoading(false);
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (loading) {
    return <div className="spinner" style={{ padding: 40 }}>Loading…</div>;
  }

  // Which of the two front doors this browser came through. On a single-host
  // deployment portalUrl is empty and this is always false — nothing changes.
  const hostOf = (u: string) => {
    try {
      return u ? new URL(u).host : '';
    } catch {
      return '';
    }
  };
  const onPortalHost = !!urls.portalUrl && window.location.host === hostOf(urls.portalUrl);

  // A staff member who landed on the tenant host is sent home — the portal host
  // is the customers' face, and the staff shell has no business being branded as
  // one particular tenant's. (Security already stops them; this is tidiness.)
  if (onPortalHost && user && user.role !== 'CUSTOMER' && urls.adminUrl) {
    window.location.replace(urls.adminUrl);
    return <div className="spinner" style={{ padding: 40 }}>Taking you to the staff console…</div>;
  }

  // Unauthenticated visitors land on the public information page; the login
  // window is invoked from there rather than being the front door. On the portal
  // host, skip the marketing page and go straight to sign-in — a customer typed
  // their portal address to sign in, not to read about the product.
  if (authRequired && !user) {
    if (onPortalHost) {
      return (
        <AuthContext.Provider value={{ user, authRequired, loading, portalUrl: urls.portalUrl, adminUrl: urls.adminUrl, refresh, logout }}>
          <EnvironmentBanner environment={env.environment} label={env.label} />
          <LoginView
            onSignedIn={refresh}
            onBack={() => undefined}
            environment={env.environment}
            environmentLabel={env.label}
          />
        </AuthContext.Provider>
      );
    }
    return (
      <>
        <EnvironmentBanner environment={env.environment} label={env.label} />
        {showLogin || demo ? (
          // The demo goes straight to sign-in: its front door is the admin
          // platform, not the marketing landing (which keeps its own dark
          // design and would land off-brand under the ZeroIncidents skin).
          <LoginView
            onSignedIn={refresh}
            onBack={() => setShowLogin(false)}
            environment={env.environment}
            environmentLabel={env.label}
          />
        ) : (
          <LandingView onSignIn={() => setShowLogin(true)} />
        )}
      </>
    );
  }

  /*
    A customer never reaches the staff shell.

    Not a hidden menu — a different application. The server refuses a CUSTOMER
    session on every staff route anyway (see the allowlist in routes/auth.ts),
    so rendering the staff shell would only produce a screen of failed requests.
    Sending them somewhere that works is both kinder and honest about what this
    account is.
  */
  if (user?.role === 'CUSTOMER') {
    return (
      <AuthContext.Provider value={{ user, authRequired, loading, portalUrl: urls.portalUrl, adminUrl: urls.adminUrl, refresh, logout }}>
        <EnvironmentBanner environment={env.environment} label={env.label} />
        <PortalView />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ user, authRequired, loading, portalUrl: urls.portalUrl, adminUrl: urls.adminUrl, refresh, logout }}>
      <DemoProvider demo={demo}>
        {!demo && <EnvironmentBanner environment={env.environment} label={env.label} />}
        <AppShell bannerOffset={demo || env.environment === 'production' ? 0 : BANNER_HEIGHT} />
      </DemoProvider>
    </AuthContext.Provider>
  );
}

/**
 * Which parts of the app actually get used. Records the view path only — the
 * session already identifies the user server-side, so nothing extra is sent.
 */
function useViewTracking() {
  const location = useLocation();
  useEffect(() => {
    fetch('/api/analytics/collect', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ event: 'view', path: location.pathname, source: 'app' }),
    }).catch(() => undefined); // never let analytics affect the app
  }, [location.pathname]);
}

/**
 * What the catalogue is divided into.
 *
 * One list, used by the sidebar and by the catalogue pages, so a section cannot
 * exist in the navigation without a page behind it or the other way round.
 *
 * `role` is the device_profiles role these map to. `browse` marks the sections
 * where a supplier range exists to choose from — only cameras so far. The rest
 * hold your own items until a vendor list arrives for them, which is a real
 * difference and not a gap to paper over: nobody should think the lens section
 * is empty because we lost the lenses.
 */
export const CATALOGUE_SECTIONS = [
  { slug: 'cameras', role: 'camera', label: 'Cameras', icon: '◎', browse: true },
  { slug: 'lenses', role: 'lens', label: 'Lenses', icon: '◍', browse: true },
  { slug: 'mounts', role: 'mount', label: 'Mounts', icon: '⊥', browse: false },
  { slug: 'lighting', role: 'light', label: 'Lighting', icon: '☀', browse: false },
  { slug: 'conveyors', role: 'conveyor', label: 'Conveyors', icon: '⇥', browse: false },
  { slug: 'rejectors', role: 'rejector', label: 'Rejectors', icon: '↗', browse: false },
  { slug: 'feeders', role: 'feeder', label: 'Feeders', icon: '⇤', browse: false },
  { slug: 'collectors', role: 'collector', label: 'Collectors', icon: '⊔', browse: false },
  { slug: 'controls', role: null, label: 'Controls & other', icon: '⚙', browse: false },
] as const;

function AppShell({ bannerOffset = 0 }: { bannerOffset?: number }) {
  useViewTracking();
  // The public demo is aimed at a prospective tenant, so its sidebar shows only
  // the functions a tenant meets in their own portal — not the internal tooling
  // (datasets, training, catalogue, customers, users, settings). Route access is
  // unchanged; this only curates what the nav offers.
  const { demo } = useDemo();
  // Which pages this installation has any use for. See api/useTasks.ts — a
  // route disappears only when no product needs it, and the pages themselves
  // narrow down further per selected product.
  const { showsRoute } = useTasks();

  /**
   * The sidebar, grouped by the job someone is doing rather than in one flat run.
   *
   * The middle group is named for what the vision system actually does. Quality
   * inspection (Annotate, Labels), classification (Classify, Classes, Confusion)
   * and label inspection are not one function — the database keeps them on
   * separate roads and so does the task registry, which hides /labels+/annotate
   * for a classification product and /classes+/classify+/confusion for a
   * quality one. But they ARE one family: three ways an AI vision system scans a
   * part and says what it is or whether it passes. So they sit under one heading,
   * each its own item, never merged into a single screen.
   *
   * `show` reads the same task registry the routes do, so a group that empties
   * out on a single-purpose install loses its heading rather than stranding it
   * over nothing.
   */
  const NAV_SECTIONS: Array<{
    title: string;
    items: Array<
      | { kind: 'link'; to: string; icon: string; label: string; hint: string; show?: boolean }
      | { kind: 'catalogue' }
    >;
  }> = [
    // Product-first and symmetric: Quality, Safety and Jakatori each read the
    // same way. Icons are one glyph family (no emoji) for a calm sidebar.
    {
      title: 'Quality',
      items: [
        { kind: 'link', to: '/live', icon: '●', label: 'Live', hint: 'Live inspection results as they come off the line' },
        { kind: 'link', to: '/dashboard', icon: '▦', label: 'Dashboard', hint: 'Pass rate, throughput and defects over a time window' },
        { kind: 'link', to: '/history', icon: '▤', label: 'History', hint: 'Every past inspection, searchable' },
        { kind: 'link', to: '/reporting', icon: '▥', label: 'Reporting', hint: "Each customer's numbers, with their products underneath" },
      ],
    },
    {
      title: 'Safety',
      items: [
        { kind: 'link', to: '/safety', icon: '⛑', label: 'Overview', hint: 'Workplace-safety monitoring — PPE, fire, near-miss on camera frames' },
        { kind: 'link', to: '/safety/live', icon: '◉', label: 'Live', hint: 'The trained models on a live camera feed — boxes drawn over the video' },
        { kind: 'link', to: '/safety/zones', icon: '⬚', label: 'Zones', hint: 'Draw the no-go zones and rules a safety camera watches' },
        { kind: 'link', to: '/safety/events', icon: '⚠', label: 'Events', hint: 'Safety events raised on the floor — intrusion, PPE, fire' },
        { kind: 'link', to: '/safety/dashboard', icon: '◱', label: 'Dashboard', hint: 'Safety events and trends over a time window' },
      ],
    },
    {
      title: 'Jakatori',
      items: [
        { kind: 'link', to: '/procguard', icon: '◈', label: 'Workstations', hint: 'Author SOPs and verify assembly/kitting steps by camera' },
      ],
    },
    {
      // The doing: an operator scanning and labelling parts, per task.
      title: 'Inspection',
      items: [
        { kind: 'link', to: '/annotate', icon: '✎', label: 'Annotate', show: showsRoute('/annotate'), hint: `Quality inspection — ${TASKS.anomaly.question}` },
        { kind: 'link', to: '/labels', icon: '▤', label: 'Etiket AI', show: showsRoute('/labels'), hint: 'Etiket AI — scan a label and check the print is correct' },
        { kind: 'link', to: '/classify', icon: '⊜', label: 'Classify', show: showsRoute('/classes'), hint: `Classification — ${TASKS.classification.question}` },
      ],
    },
    {
      // The building: data in, models out — plus the model-analysis screens.
      title: 'Models',
      items: [
        { kind: 'link', to: '/dataset', icon: '⬆', label: 'Dataset', hint: 'Training images, grouped into sets per customer' },
        { kind: 'link', to: '/training', icon: '⚗', label: 'Training', hint: 'Train and compare models on a dataset' },
        { kind: 'link', to: '/gpu', icon: '⚡', label: 'Cloud GPU', hint: 'Rent a cloud GPU to train the vision models; stage datasets and watch instances live' },
        { kind: 'link', to: '/model-analytics', icon: '◑', label: 'Model analytics', hint: 'Speed, verdicts and human agreement, per model version' },
        { kind: 'link', to: '/classes', icon: '⊞', label: 'Classes', show: showsRoute('/classes'), hint: 'The classes a classification model chooses between' },
        { kind: 'link', to: '/confusion', icon: '⊠', label: 'Confusion', show: showsRoute('/classes'), hint: 'Where the model confuses one class for another' },
      ],
    },
    {
      title: 'Equipment',
      items: [
        { kind: 'catalogue' },
        { kind: 'link', to: '/proposals', icon: '✎', label: 'Proposals', hint: 'Line design proposals and quotes' },
        { kind: 'link', to: '/line', icon: '⇥', label: 'Line', hint: 'The equipment installed on a line, in belt order' },
        { kind: 'link', to: '/fleet', icon: '⛁', label: 'Fleet', hint: 'Are the stations still talking to us, and healthy?' },
      ],
    },
    {
      title: 'Business',
      items: [
        { kind: 'link', to: '/customers', icon: '⌂', label: 'Customers', hint: 'The companies whose products you inspect' },
        { kind: 'link', to: '/users', icon: '⚇', label: 'Users', hint: 'Who can sign in, and what they may do' },
      ],
    },
    {
      title: 'Marketing',
      items: [
        { kind: 'link', to: '/website', icon: '▤', label: 'Website', hint: 'Edit the public marketing site' },
        { kind: 'link', to: '/hero-swirl', icon: '❉', label: 'Hero swirl', hint: 'Mix the colours and motion of the marketing hero background, with a live preview' },
        { kind: 'link', to: '/analytics', icon: '◔', label: 'Web analytics', hint: 'Website reach and product usage, from our own data' },
      ],
    },
    {
      title: 'System',
      items: [
        { kind: 'link', to: '/settings', icon: '⚙', label: 'Settings', hint: 'Products, thresholds, hardware and system settings' },
        { kind: 'link', to: '/byoa-guide', icon: '✦', label: 'BYOA guide', hint: 'How a customer connects their own AI agent — endpoint, keys, tools' },
      ],
    },
  ];

  // In the demo, keep only the functions an admin ticked in Settings (stored on
  // the server, defaulting to the tenant-facing set). Empty sections drop out so
  // no bare heading is left behind.
  const [demoNav, setDemoNav] = useState<string[]>(DEFAULT_DEMO_NAV);
  useEffect(() => {
    if (!demo) return;
    fetch('/api/demo-nav')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => Array.isArray(d?.enabled) && setDemoNav(d.enabled))
      .catch(() => undefined);
  }, [demo]);
  const allowed = new Set(demoNav);
  const navSections = demo
    ? NAV_SECTIONS.map((s) => ({
        ...s,
        items: s.items.filter((it) => it.kind === 'link' && allowed.has(it.to)),
      })).filter((s) => s.items.length > 0)
    : NAV_SECTIONS;

  const { pathname } = useLocation();
  const onCatalogue = pathname.startsWith('/catalogue');

  /**
   * Whether the catalogue's nine sub-items are showing.
   *
   * Remembered across reloads, because it is a preference about how you work
   * rather than a property of the page you happen to be on. Open by default:
   * a menu that hides its contents on first acquaintance teaches people the
   * items are not there.
   */
  const [catalogueOpen, setCatalogueOpen] = useState(() => {
    try {
      return localStorage.getItem('nav.catalogue') !== 'closed';
    } catch {
      // Private browsing throws on localStorage. A nav that cannot render
      // because it cannot read a preference is worse than a forgotten one.
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('nav.catalogue', catalogueOpen ? 'open' : 'closed');
    } catch {
      /* see above */
    }
  }, [catalogueOpen]);

  /**
   * A link straight to a sub-page opens the group holding it.
   *
   * Without this, following a link to /catalogue/lenses with the group folded
   * lands you on the page with its own menu entry hidden — the nav then
   * disagrees with the screen about where you are.
   */
  useEffect(() => {
    if (pathname.startsWith('/catalogue/')) setCatalogueOpen(true);
  }, [pathname]);

  return (
    <div className="app" style={bannerOffset ? { paddingTop: bannerOffset } : undefined}>
      <nav className="sidebar">
        <div className="brand">
          <Wordmark />
        </div>
        {navSections.map((section) => {
          // A section header only appears over something. showsRoute() can empty
          // a whole group on a pure-anomaly install, and a heading with nothing
          // under it reads as a page that failed to load rather than one that
          // does not apply here.
          const visible = section.items.filter(
            (it) => it.kind === 'catalogue' || it.show !== false,
          );
          if (visible.length === 0) return null;
          return (
            <div className="nav-group" key={section.title}>
              <div className="nav-section">{section.title}</div>
              {visible.map((it) =>
                it.kind === 'catalogue' ? (
                  <Fragment key="catalogue">
                    <NavLink
                      to="/catalogue"
                      end
                      className="nav-link"
                      onClick={() => {
                        /*
                          Clicking Catalogue while it is already open folds it
                          shut. Nine sub-items is most of a screen; someone not
                          specifying hardware today should not scroll past all of
                          them. The click still navigates — the fold is what
                          changes on the second one, by which point you are
                          already on the page.
                        */
                        if (catalogueOpen && onCatalogue) setCatalogueOpen(false);
                        else setCatalogueOpen(true);
                      }}
                    >
                      <span style={{ flex: 1 }}>⊟ Catalogue</span>
                      <span aria-hidden style={{ fontSize: 12, opacity: 0.7 }}>
                        {catalogueOpen ? '▾' : '▸'}
                      </span>
                    </NavLink>
                    {catalogueOpen &&
                      CATALOGUE_SECTIONS.map((c) => (
                        <NavLink key={c.slug} to={`/catalogue/${c.slug}`} className="nav-link sub">
                          <span className="nav-ico" aria-hidden>{c.icon}</span> {c.label}
                        </NavLink>
                      ))}
                  </Fragment>
                ) : (
                  <NavLink key={it.to} to={it.to} className="nav-link" title={it.hint}>
                    <span className="nav-ico" aria-hidden>{it.icon}</span> {it.label}
                  </NavLink>
                ),
              )}
            </div>
          );
        })}
        <div className="nav-spacer" />
        <HealthDot />
        <UserChip />
        <BuildStamp />
      </nav>
      {/* The first path segment stamps a signature colour on the whole view via
          CSS (--tone per data-view in index.css); every panel and tile inside
          inherits it unless it sets its own. */}
      <main className="main" data-view={pathname.split('/')[1] || 'home'}>
        <ViewErrorBoundary key={pathname}>
        <Routes>
          <Route path="/" element={<Navigate to="/live" replace />} />
          <Route path="/live" element={<LiveView />} />
          <Route path="/dashboard" element={<DashboardView />} />
          <Route path="/history" element={<HistoryView />} />
          <Route path="/reporting" element={<ReportingView />} />
          <Route path="/safety" element={<SafetyView />} />
          <Route path="/safety/live" element={<SafetyLiveView />} />
          <Route path="/safety/zones" element={<SafetyZonesView />} />
          <Route path="/safety/events" element={<SafetyEventsView />} />
          <Route path="/safety/dashboard" element={<SafetyDashboardView />} />
          <Route path="/dataset" element={<DatasetView />} />
          <Route path="/annotate" element={<AnnotateView />} />
          <Route path="/labels" element={<LabelsView />} />
          <Route path="/classes" element={<ClassesView />} />
          <Route path="/classify" element={<ClassifyView />} />
          <Route path="/confusion" element={<ConfusionView />} />
          <Route path="/training" element={<TrainingView />} />
          <Route path="/gpu" element={<GpuTrainingView />} />
          <Route path="/analytics" element={<AnalyticsView />} />
          <Route path="/model-analytics" element={<ModelAnalytics />} />
          <Route path="/catalogue" element={<CatalogueView />} />
          {/* Detail lives under /item/ so a category slug can never be
              parsed as an id — "/catalogue/cameras" would otherwise ask the
              API for profile number NaN. */}
          <Route path="/catalogue/item/:id" element={<CatalogueDetail />} />
          <Route path="/catalogue/:category" element={<CatalogueView />} />
          <Route path="/catalogue/cameras/find" element={<CameraPickerView />} />
          <Route path="/catalogue/lenses/find" element={<LensPickerView />} />
          <Route path="/proposals" element={<DesignsView />} />
          <Route path="/line" element={<LineView />} />
          <Route path="/fleet" element={<FleetView />} />
          <Route path="/website" element={<CmsView />} />
          <Route path="/customers" element={<CustomersView />} />
          <Route path="/users" element={<UsersView />} />
          <Route path="/settings" element={<SettingsView />} />
          <Route path="/byoa-guide" element={<ByoaGuideView />} />
          <Route path="/hero-swirl" element={<HeroSwirlView />} />
          <Route path="/procguard" element={<ProcGuardView />} />
          <Route path="/procguard/parts" element={<ProcGuardPartsView />} />
          <Route path="/procguard/cycles" element={<ProcGuardCyclesView />} />
          <Route path="/procguard/dashboard" element={<ProcGuardDashboardView />} />
          <Route path="/procguard/procedure/:id" element={<ProcGuardProcedureEditor />} />
          <Route path="/procguard/kiosk/:id" element={<ProcGuardKioskView />} />
        </Routes>
        </ViewErrorBoundary>
      </main>
      {/* visai rides above every page rather than living on one — the question
          occurs to you while you are looking at the thing it is about. */}
      <VisaiWidget />
    </div>
  );
}
